import React, { useState, useEffect } from 'react';
import Banner from '../components/common/Banner';
import { apiService } from '../services/api';
import { getImageUrl } from '../utils/imageUtils';

const EventsPage = () => {
  const [events, setEvents] = useState([]);
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');

  useEffect(() => {
    Promise.all([
      fetchEvents(),
      fetchEventsPageContent()
    ]).finally(() => setLoading(false));
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await apiService.getEvents({
        status: 'published',
        limit: 20,
        sort: '-startDate'
      });
      setEvents(response.data.events || []);
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  };

  const fetchEventsPageContent = async () => {
    try {
      const response = await apiService.getEventsPageContent();
      setContent(response.data);
    } catch (error) {
      setError('Failed to load page content');
      console.error('Error fetching events page content:', error);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getEventTypeIcon = (eventType) => {
    switch (eventType) {
      case 'seminar': return 'fas fa-presentation';
      case 'workshop': return 'fas fa-tools';
      case 'conference': return 'fas fa-users';
      case 'cultural': return 'fas fa-theater-masks';
      case 'sports': return 'fas fa-trophy';
      case 'technical': return 'fas fa-code';
      case 'academic': return 'fas fa-graduation-cap';
      case 'placement': return 'fas fa-briefcase';
      default: return 'fas fa-calendar';
    }
  };

  const getEventTypeColor = (eventType) => {
    switch (eventType) {
      case 'seminar': return 'bg-blue-100 text-blue-800';
      case 'workshop': return 'bg-green-100 text-green-800';
      case 'conference': return 'bg-purple-100 text-purple-800';
      case 'cultural': return 'bg-pink-100 text-pink-800';
      case 'sports': return 'bg-orange-100 text-orange-800';
      case 'technical': return 'bg-gray-100 text-gray-800';
      case 'academic': return 'bg-indigo-100 text-indigo-800';
      case 'placement': return 'bg-crimson bg-opacity-10 text-crimson';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredEvents = events.filter(event => 
    activeFilter === 'all' || event.eventType === activeFilter
  );

  const eventTypes = [...new Set(events.map(event => event.eventType))];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-slate">
          <i className="fas fa-spinner fa-spin mr-2 text-crimson"></i>
          Loading content...
        </div>
      </div>
    );
  }

  if (!content) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <i className="fas fa-exclamation-circle text-4xl text-red-500 mb-4"></i>
          <p className="text-red-600">Failed to load page content</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full fade-in">
      <Banner 
        title={content.bannerTitle || "Events & Happenings"}
        subtitle={content.bannerSubtitle || "Campus Life | Student Activities | Community Engagement"}
        imageSrc={content.bannerImage ? (content.bannerImage.startsWith('http') ? content.bannerImage : getImageUrl(content.bannerImage)) : "https://images.unsplash.com/photo-1523580494863-6f3031224c94?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80"}
      />
      <div className="max-w-6xl mx-auto p-4 sm:p-6 md:p-10 space-y-8 sm:space-y-12">
        
        <div className="mb-6 sm:mb-8">
          <h2 className="text-2xl sm:text-3xl font-sans font-bold text-slate mb-3 sm:mb-4">{content.pageIntroTitle || "Events & Happenings"}</h2>
          <p className="text-gray-500">{content.pageIntroDescription || "Celebrating achievements, learning, and community engagement through various campus activities."}</p>
        </div>

        {/* Admin Events Section */}
        {!loading && events.length > 0 && (
          <section>
            <div className="mb-6">
              <h3 className="text-xl sm:text-2xl font-bold text-crimson mb-4 flex items-center">
                <i className="fas fa-calendar mr-3"></i>
                Upcoming Events
              </h3>
              
              {/* Event Type Filters */}
              <div className="flex flex-wrap gap-2 mb-6">
                <button
                  onClick={() => setActiveFilter('all')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activeFilter === 'all' 
                      ? 'bg-crimson text-white' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  All Events
                </button>
                {eventTypes.map(type => (
                  <button
                    key={type}
                    onClick={() => setActiveFilter(type)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors capitalize ${
                      activeFilter === type 
                        ? 'bg-crimson text-white' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {type}
                  </button>
                ))}
              </div>
            </div>

            {loading ? (
              <div className="text-center py-8">
                <i className="fas fa-spinner fa-spin text-2xl text-crimson mb-2"></i>
                <p className="text-gray-600">Loading events...</p>
              </div>
            ) : error ? (
              <div className="text-center py-8">
                <i className="fas fa-exclamation-circle text-2xl text-red-500 mb-2"></i>
                <p className="text-red-600">{error}</p>
              </div>
            ) : filteredEvents.length > 0 ? (
              <div className="grid sm:grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                {filteredEvents.map((event) => (
                  <div key={event.id} className="bg-white p-4 sm:p-6 rounded-xl border border-border_gray hover:shadow-lg transition-shadow">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center space-x-2">
                        <i className={`${getEventTypeIcon(event.eventType)} text-crimson`}></i>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${getEventTypeColor(event.eventType)}`}>
                          {event.eventType}
                        </span>
                        {event.featured && (
                          <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-medium">
                            <i className="fas fa-star mr-1"></i>
                            Featured
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <h4 className="font-bold text-slate mb-2 text-lg">{event.title}</h4>
                    <p className="text-gray-600 text-sm mb-3 line-clamp-2">{event.description}</p>
                    
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center text-sm text-gray-500">
                        <i className="fas fa-calendar-alt w-4 mr-2"></i>
                        <span>{formatDate(event.startDate)}</span>
                        {event.endDate && event.startDate !== event.endDate && (
                          <span> - {formatDate(event.endDate)}</span>
                        )}
                      </div>
                      
                      {(event.startTime || event.endTime) && (
                        <div className="flex items-center text-sm text-gray-500">
                          <i className="fas fa-clock w-4 mr-2"></i>
                          <span>
                            {event.startTime && formatTime(event.startTime)}
                            {event.endTime && event.startTime && ' - '}
                            {event.endTime && formatTime(event.endTime)}
                          </span>
                        </div>
                      )}
                      
                      <div className="flex items-center text-sm text-gray-500">
                        <i className="fas fa-map-marker-alt w-4 mr-2"></i>
                        <span>{event.venue}</span>
                      </div>

                      {event.organizer && event.organizer.department && (
                        <div className="flex items-center text-sm text-gray-500">
                          <i className="fas fa-users w-4 mr-2"></i>
                          <span>{event.organizer.department}</span>
                        </div>
                      )}
                    </div>

                    {event.registrationRequired && (
                      <div className="bg-blue-50 p-3 rounded-lg border border-blue-200 mb-4">
                        <div className="flex items-center text-blue-800 text-sm font-medium mb-1">
                          <i className="fas fa-user-plus mr-2"></i>
                          Registration Required
                        </div>
                        {event.registrationDeadline && (
                          <div className="text-blue-600 text-xs">
                            Deadline: {formatDate(event.registrationDeadline)}
                          </div>
                        )}
                        {event.maxParticipants && (
                          <div className="text-blue-600 text-xs">
                            Max Participants: {event.maxParticipants}
                          </div>
                        )}
                      </div>
                    )}

                    {event.tags && event.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-4">
                        {event.tags.slice(0, 3).map((tag) => (
                          <span key={tag} className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">
                            {tag}
                          </span>
                        ))}
                        {event.tags.length > 3 && (
                          <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">
                            +{event.tags.length - 3}
                          </span>
                        )}
                      </div>
                    )}

                    <div className="flex items-center justify-between">
                      <div className="text-xs text-gray-400">
                        Views: {event.views || 0}
                      </div>
                      {event.organizer && event.organizer.email && (
                        <a
                          href={`mailto:${event.organizer.email}`}
                          className="text-crimson hover:text-red-800 text-sm font-medium"
                        >
                          <i className="fas fa-envelope mr-1"></i>
                          Contact
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <i className="fas fa-calendar text-4xl text-gray-300 mb-4"></i>
                <h4 className="text-xl font-semibold text-gray-600 mb-2">No events found</h4>
                <p className="text-gray-500">Check back later for upcoming events and activities.</p>
              </div>
            )}
          </section>
        )}

        {/* College Happenings - Admin Managed Content */}
        <section>
          <div className="mb-6">
            <h3 className="text-xl sm:text-2xl font-bold text-crimson mb-4 flex items-center">
              <i className="fas fa-images mr-3"></i>
              {content.collegeLifeTitle || "College Life & Activities"}
            </h3>
            <p className="text-gray-500">{content.collegeLifeSubtitle || "Regular activities and initiatives that make our campus vibrant and engaging."}</p>
          </div>
        </section>

        {/* Students Trip - Admin Managed */}
        <section>
          <div className="bg-white p-4 sm:p-6 md:p-8 rounded-xl border border-border_gray">
            <h3 className="text-xl sm:text-2xl font-bold text-crimson mb-4 sm:mb-6 flex items-center">
              <i className="fas fa-bus mr-3"></i>
              {content.studentsTrip?.title || "Educational Trips & Field Visits"}
            </h3>
            
            <p className="text-gray-600 leading-relaxed text-sm sm:text-base mb-6">
              {content.studentsTrip?.description || "Our students regularly participate in educational trips and industrial visits to leading companies and research institutions. These experiences provide practical exposure to real-world applications of theoretical knowledge and help students understand industry practices and technological advancements."}
            </p>

            {/* Industrial Visit Photos */}
            {content.studentsTrip?.industrialVisitPhotos && content.studentsTrip.industrialVisitPhotos.length > 0 && (
              <div className="mb-8">
                <h4 className="text-lg font-semibold text-slate mb-4 flex items-center">
                  <i className="fas fa-industry mr-2"></i>
                  Industrial Visit Photos
                </h4>
                <div className="grid sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {content.studentsTrip.industrialVisitPhotos.map((image, index) => (
                    <div key={index} className="bg-gray-100 rounded-lg h-48 overflow-hidden group hover:shadow-lg transition-shadow">
                      {image.url ? (
                        <img 
                          src={image.url ? (image.url.startsWith('http') ? image.url : getImageUrl(image.url)) : ''} 
                          alt={image.alt || image.title || "Industrial Visit"} 
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" 
                        />
                      ) : (
                        <div className="h-full flex items-center justify-center text-gray-500">
                          <div className="text-center">
                            <i className="fas fa-camera text-4xl mb-2"></i>
                            <p className="text-sm">{image.title || "Industrial Visit Photo"}</p>
                          </div>
                        </div>
                      )}
                      {image.title && image.url && (
                        <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white p-2">
                          <p className="text-xs truncate">{image.title}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Technical Tour Gallery */}
            {content.studentsTrip?.technicalTourGallery && content.studentsTrip.technicalTourGallery.length > 0 && (
              <div className="mb-8">
                <h4 className="text-lg font-semibold text-slate mb-4 flex items-center">
                  <i className="fas fa-cogs mr-2"></i>
                  Technical Tour Gallery
                </h4>
                <div className="grid sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {content.studentsTrip.technicalTourGallery.map((image, index) => (
                    <div key={index} className="bg-gray-100 rounded-lg h-48 overflow-hidden group hover:shadow-lg transition-shadow relative">
                      {image.url ? (
                        <img 
                          src={image.url ? (image.url.startsWith('http') ? image.url : getImageUrl(image.url)) : ''} 
                          alt={image.alt || image.title || "Technical Tour"} 
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" 
                        />
                      ) : (
                        <div className="h-full flex items-center justify-center text-gray-500">
                          <div className="text-center">
                            <i className="fas fa-camera text-4xl mb-2"></i>
                            <p className="text-sm">{image.title || "Technical Tour Photo"}</p>
                          </div>
                        </div>
                      )}
                      {image.title && image.url && (
                        <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white p-2">
                          <p className="text-xs truncate">{image.title}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Study Tour Highlights */}
            {content.studentsTrip?.studyTourHighlights && content.studentsTrip.studyTourHighlights.length > 0 && (
              <div className="mb-6">
                <h4 className="text-lg font-semibold text-slate mb-4 flex items-center">
                  <i className="fas fa-graduation-cap mr-2"></i>
                  Study Tour Highlights
                </h4>
                <div className="grid sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {content.studentsTrip.studyTourHighlights.map((image, index) => (
                    <div key={index} className="bg-gray-100 rounded-lg h-48 overflow-hidden group hover:shadow-lg transition-shadow relative">
                      {image.url ? (
                        <img 
                          src={image.url ? (image.url.startsWith('http') ? image.url : getImageUrl(image.url)) : ''} 
                          alt={image.alt || image.title || "Study Tour"} 
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" 
                        />
                      ) : (
                        <div className="h-full flex items-center justify-center text-gray-500">
                          <div className="text-center">
                            <i className="fas fa-camera text-4xl mb-2"></i>
                            <p className="text-sm">{image.title || "Study Tour Photo"}</p>
                          </div>
                        </div>
                      )}
                      {image.title && image.url && (
                        <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white p-2">
                          <p className="text-xs truncate">{image.title}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Fallback for no images */}
            {(!content.studentsTrip?.industrialVisitPhotos?.length && 
              !content.studentsTrip?.technicalTourGallery?.length && 
              !content.studentsTrip?.studyTourHighlights?.length) && (
              <div className="text-center py-8 text-gray-500">
                <i className="fas fa-images text-4xl mb-3"></i>
                <p>Photo gallery coming soon</p>
              </div>
            )}
          </div>
        </section>

        {/* Tree Plantation - Admin Managed */}
        <section>
          <div className="bg-white p-4 sm:p-6 md:p-8 rounded-xl border border-border_gray">
            <h3 className="text-xl sm:text-2xl font-bold text-crimson mb-4 sm:mb-6 flex items-center">
              <i className="fas fa-tree mr-3"></i>
              {content.treePlantation?.title || "Environmental Initiatives - Tree Plantation"}
            </h3>
            
            {/* Content First */}
            <div className="mb-6">
              <p className="text-gray-600 leading-relaxed text-sm sm:text-base mb-4">
                {content.treePlantation?.description || "As part of our commitment to environmental sustainability, FiT Engineering College regularly organizes tree plantation drives. These initiatives involve students, faculty, and staff working together to create a greener campus and contribute to environmental conservation."}
              </p>
              
              {content.treePlantation?.impact && (
                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <h4 className="font-semibold text-green-800 mb-2">{content.treePlantation.impact.title}</h4>
                  <div className="grid grid-cols-2 gap-4 text-green-700 text-center">
                    {content.treePlantation.impact.stats?.map((stat, index) => (
                      <div key={index}>
                        <div className="text-2xl font-bold">{stat.value}</div>
                        <div className="text-sm">{stat.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            {/* Tree Plantation Gallery Below */}
            {content.treePlantation?.images && content.treePlantation.images.length > 0 ? (
              <div className="space-y-4">
                <h4 className="text-lg font-semibold text-slate flex items-center">
                  <i className="fas fa-seedling mr-2"></i>
                  Tree Plantation Gallery
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                  {content.treePlantation.images.map((image, index) => (
                    <div key={index} className="bg-gray-100 rounded-lg h-48 overflow-hidden group hover:shadow-lg transition-shadow relative">
                      {image.url ? (
                        <img 
                          src={image.url ? (image.url.startsWith('http') ? image.url : getImageUrl(image.url)) : ''} 
                          alt={image.alt || image.title || "Tree Plantation"} 
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" 
                        />
                      ) : (
                        <div className="h-full flex items-center justify-center text-gray-500">
                          <div className="text-center">
                            <i className="fas fa-camera text-4xl mb-2"></i>
                            <p className="text-sm">{image.title || "Tree Plantation Photo"}</p>
                          </div>
                        </div>
                      )}
                      {image.title && image.url && (
                        <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white p-2">
                          <p className="text-xs truncate">{image.title}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="bg-gray-100 p-4 rounded-lg h-64 flex items-center justify-center">
                <div className="text-center text-gray-500">
                  <i className="fas fa-camera text-5xl mb-3"></i>
                  <p>Tree Plantation Drive Photos</p>
                  <p className="text-xs mt-1">Photos coming soon</p>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Tab Distributions - Admin Managed */}
        <section>
          <div className="bg-white p-4 sm:p-6 md:p-8 rounded-xl border border-border_gray">
            <h3 className="text-xl sm:text-2xl font-bold text-crimson mb-4 sm:mb-6 flex items-center">
              <i className="fas fa-tablet-alt mr-3"></i>
              {content.tabDistribution?.title || "Digital Learning Initiative - Device Distribution"}
            </h3>
            
            {/* Content First */}
            <div className="mb-6">
              <p className="text-gray-600 leading-relaxed text-sm sm:text-base mb-4">
                {content.tabDistribution?.description || "To enhance digital learning and ensure equal access to educational resources, the institute has undertaken a comprehensive device distribution program. Students receive tablets and digital learning tools to support their academic journey and bridge the digital divide."}
              </p>
              
              {content.tabDistribution?.benefits && (
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <h4 className="font-semibold text-blue-800 mb-2">{content.tabDistribution.benefits.title}</h4>
                  <ul className="text-blue-700 text-sm space-y-1">
                    {content.tabDistribution.benefits.items?.map((item, index) => (
                      <li key={index}>• {item}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            
            {/* Digital Learning Gallery Below */}
            {content.tabDistribution?.images && content.tabDistribution.images.length > 0 ? (
              <div className="space-y-4">
                <h4 className="text-lg font-semibold text-slate flex items-center">
                  <i className="fas fa-laptop mr-2"></i>
                  Digital Learning Gallery
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                  {content.tabDistribution.images.map((image, index) => (
                    <div key={index} className="bg-gray-100 rounded-lg h-48 overflow-hidden group hover:shadow-lg transition-shadow relative">
                      {image.url ? (
                        <img 
                          src={image.url ? (image.url.startsWith('http') ? image.url : getImageUrl(image.url)) : ''} 
                          alt={image.alt || image.title || "Digital Learning"} 
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" 
                        />
                      ) : (
                        <div className="h-full flex items-center justify-center text-gray-500">
                          <div className="text-center">
                            <i className="fas fa-camera text-4xl mb-2"></i>
                            <p className="text-sm">{image.title || "Digital Learning Photo"}</p>
                          </div>
                        </div>
                      )}
                      {image.title && image.url && (
                        <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white p-2">
                          <p className="text-xs truncate">{image.title}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="bg-gray-100 p-4 rounded-lg h-64 flex items-center justify-center">
                <div className="text-center text-gray-500">
                  <i className="fas fa-camera text-5xl mb-3"></i>
                  <p>Device Distribution Ceremony Photos</p>
                  <p className="text-xs mt-1">Photos coming soon</p>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Yoga Day - Admin Managed */}
        <section>
          <div className="bg-white p-4 sm:p-6 md:p-8 rounded-xl border border-border_gray">
            <h3 className="text-xl sm:text-2xl font-bold text-crimson mb-4 sm:mb-6 flex items-center">
              <i className="fas fa-meditation mr-3"></i>
              {content.yogaDay?.title || "International Yoga Day Celebration"}
            </h3>
            
            <p className="text-gray-600 leading-relaxed text-sm sm:text-base mb-6">
              {content.yogaDay?.description || "Every year, our institute celebrates International Yoga Day with enthusiasm and participation from the entire campus community. These sessions promote physical and mental well-being, stress management, and mindfulness among students and faculty members."}
            </p>

            {/* Yoga Day Images */}
            {content.yogaDay?.images && content.yogaDay.images.length > 0 ? (
              <div className="space-y-4">
                <h4 className="text-lg font-semibold text-slate flex items-center">
                  <i className="fas fa-heart mr-2"></i>
                  Yoga Day Gallery
                </h4>
                <div className="grid sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {content.yogaDay.images.map((image, index) => (
                    <div key={index} className="bg-gray-100 rounded-lg h-48 overflow-hidden group hover:shadow-lg transition-shadow relative">
                      {image.url ? (
                        <img 
                          src={image.url ? (image.url.startsWith('http') ? image.url : getImageUrl(image.url)) : ''} 
                          alt={image.alt || image.title || "Yoga Day Celebration"} 
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" 
                        />
                      ) : (
                        <div className="h-full flex items-center justify-center text-gray-500">
                          <div className="text-center">
                            <i className="fas fa-camera text-4xl mb-2"></i>
                            <p className="text-sm">{image.title || "Yoga Day Photo"}</p>
                          </div>
                        </div>
                      )}
                      {image.title && image.url && (
                        <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white p-2">
                          <p className="text-xs truncate">{image.title}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <i className="fas fa-images text-4xl mb-3"></i>
                <p>Yoga Day photo gallery coming soon</p>
              </div>
            )}
          </div>
        </section>

        {/* Sports - Admin Managed */}
        <section>
          <div className="bg-white p-4 sm:p-6 md:p-8 rounded-xl border border-border_gray">
            <h3 className="text-xl sm:text-2xl font-bold text-crimson mb-4 sm:mb-6 flex items-center">
              <i className="fas fa-trophy mr-3"></i>
              {content.sports?.title || "Student Activities"}
            </h3>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 mb-6">
              {content.sports?.activities?.map((activity, index) => (
                <div key={index} className="bg-gray-100 p-4 rounded-lg h-40 flex items-center justify-center">
                  <div className="text-center text-gray-500">
                    <i className={`fas fa-${activity.icon} text-3xl mb-2`}></i>
                    <p className="text-sm">{activity.title}</p>
                  </div>
                </div>
              ))}
            </div>
            
            <p className="text-gray-600 leading-relaxed mb-6 text-sm sm:text-base">
              {content.sports?.description || "Sports play a vital role in the overall development of our students. The institute encourages participation in various sports activities and regularly organizes tournaments, championships, and fitness programs to promote a healthy and active lifestyle."}
            </p>

            {/* Sports Activity Images */}
            {content.sports?.images && content.sports.images.length > 0 && (
              <div className="mb-8">
                <h4 className="text-lg font-semibold text-slate mb-4 flex items-center">
                  <i className="fas fa-camera-retro mr-2"></i>
                  Student Activity Gallery
                </h4>
                <div className="grid sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {content.sports.images.map((image, index) => (
                    <div key={index} className="bg-gray-100 rounded-lg h-48 overflow-hidden group hover:shadow-lg transition-shadow relative">
                      {image.url ? (
                        <img 
                          src={image.url ? (image.url.startsWith('http') ? image.url : getImageUrl(image.url)) : ''} 
                          alt={image.alt || image.title || "Sports Activity"} 
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" 
                        />
                      ) : (
                        <div className="h-full flex items-center justify-center text-gray-500">
                          <div className="text-center">
                            <i className="fas fa-camera text-4xl mb-2"></i>
                            <p className="text-sm">{image.title || "Sports Activity Photo"}</p>
                          </div>
                        </div>
                      )}
                      {image.title && image.url && (
                        <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white p-2">
                          <p className="text-xs truncate">{image.title}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="grid sm:grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 lg:gap-8">
              <div>
                {content.sports?.facilities && (
                  <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                    <h4 className="font-semibold text-yellow-800 mb-2">{content.sports.facilities.title}</h4>
                    <ul className="text-yellow-700 text-sm space-y-1">
                      {content.sports.facilities.items?.map((item, index) => (
                        <li key={index}>• {item}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              
              <div>
                {content.sports?.achievements && (
                  <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                    <h4 className="font-semibold text-orange-800 mb-2">{content.sports.achievements.title}</h4>
                    <ul className="text-orange-700 text-sm space-y-1">
                      {content.sports.achievements.items?.map((item, index) => (
                        <li key={index}>• {item}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Call to Action - Admin Managed */}
        <section>
          <div className="bg-slate-50 p-4 sm:p-6 md:p-8 rounded-xl border border-border_gray text-center">
            <h3 className="text-xl sm:text-2xl font-sans font-bold text-slate mb-3 sm:mb-4">{content.ctaSection?.title || "Stay Connected"}</h3>
            <p className="text-gray-600 mb-4 sm:mb-6 text-sm sm:text-base">
              {content.ctaSection?.description || "Don't miss out on our latest events and activities. Follow us on social media for regular updates."}
            </p>
            <div className="flex justify-center gap-3 sm:gap-4 flex-wrap">
              {content.ctaSection?.socialLinks && (
                <>
                  <a href={content.ctaSection.socialLinks.facebook || "#"} className="bg-crimson hover:bg-red-800 text-white p-3 rounded-full transition">
                    <i className="fab fa-facebook-f"></i>
                  </a>
                  <a href={content.ctaSection.socialLinks.instagram || "#"} className="bg-crimson hover:bg-red-800 text-white p-3 rounded-full transition">
                    <i className="fab fa-instagram"></i>
                  </a>
                  <a href={content.ctaSection.socialLinks.youtube || "#"} className="bg-crimson hover:bg-red-800 text-white p-3 rounded-full transition">
                    <i className="fab fa-youtube"></i>
                  </a>
                  <a href={content.ctaSection.socialLinks.linkedin || "#"} className="bg-crimson hover:bg-red-800 text-white p-3 rounded-full transition">
                    <i className="fab fa-linkedin-in"></i>
                  </a>
                </>
              )}
            </div>
          </div>
        </section>

      </div>
    </div>
  );
};

export default EventsPage;